import React from "react";
import { Icon, Portal } from "./registry";
import { fmtMeasure } from "./lib/verticals";   // "180 ft" / "210 sq ft"
/* ============================================================
   Fence Works — global search (⌘K-style), searches everything
   ============================================================ */
function GlobalSearch({ store, go }: any) {
  const [open, setOpen] = React.useState<any>(false);
  const [q, setQ] = React.useState<any>("");
  const [active, setActive] = React.useState<any>(0);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) { e.preventDefault(); setOpen(true); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  React.useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
  React.useEffect(() => { setActive(0); }, [q]);

  const index = React.useMemo(() => {
    const items = [];
    // pages
    [["Dashboard", "dashboard", "dashboard"], ["To-Do", "check", "todo"], ["Estimator", "map", "estimator"], ["Estimates", "send", "estimates"],
     ["Work Orders", "clipboard", "workorders"], ["Schedule", "calendar", "calendar"], ["Customers", "user", "customers"],
     ["Lead Radar", "target", "leadradar"], ["Reviews", "chat", "reviews"], ["Google Profile", "mappin", "gbp"], ["Website", "building", "website"], ["Crew Mode", "bolt", "crew"], ["Messages", "mail", "messages"], ["Job Packet", "book", "jobpacket"],
     ["Products", "box", "products"], ["Inventory", "box", "inventory"], ["Purchase Orders", "clipboard", "purchaseorders"],
     ["Reports", "activity", "reports"], ["Sales Team", "user", "salesteam"], ["Budget & Cash", "dollar", "business"], ["TV Display", "activity", "tv"],
     ["AI Front Desk", "phone", "ai"], ["Visualizer", "bolt", "visualizer"], ["Specs", "ruler", "specs"], ["Codes", "book", "codes"],
     ["3D Models", "layers", "models"], ["Permit Check", "mappin", "permits"], ["Email Sequences", "mail", "sequences"],
     ["Financing", "dollar", "financing"], ["Integrations", "bolt", "integrations"], ["Customer Hub", "user", "hub"],
     ["Estimator Widget", "map", "widget"], ["Account & Billing", "dollar", "account"], ["Guide", "book", "guide"],
     ["Import & export", "download", "dataio"]].forEach(([label, icon, route]) =>
      items.push({ kind: "Page", label, sub: "Go to " + label, icon, go: () => go(route) }));
    [["Company Settings", "company"], ["Pricing & Markup", "pricing"], ["Sales Tax & Fees", "tax"], ["Quoting defaults", "quoting"], ["Crews & Labor", "crews"],
     ["Social profiles", "social"], ["Financing", "financing"], ["Parcel Data Source", "parcel"], ["Data Safety & Backup", "backup"]].forEach(([label, section]) =>
      items.push({ kind: "Settings", label, sub: "Settings · " + label, icon: "gear", go: () => go("settings", { section }) }));
    [["Team & seats", "account", { tab: "team" }], ["Send feedback", "feedback", null]].forEach(([label, route, params]: any) =>
      items.push({ kind: "Action", label, sub: route === "feedback" ? "Tell the Fence Works team what's broken or missing" : "Invite teammates, manage seats", icon: route === "feedback" ? "chat" : "user",
        go: () => route === "feedback" ? window.dispatchEvent(new Event("fw:help-open")) : go(route, params) }));
    // customers
    (store.customers || []).forEach(c => items.push({ kind: "Customer", label: c.name || "Customer", sub: [c.email, c.phone, c.address].filter(Boolean).join(" · ") || "Customer", icon: "user", go: () => go("customers", { focus: c.id }) }));
    // quotes
    (store.quotes || []).forEach(qq => items.push({ kind: "Quote", label: (qq.customer || "Quote") + " — " + window.VFP.money0(qq.total), sub: qq.productName + " · " + fmtMeasure(qq) + " · " + qq.status, icon: "send", go: () => go("quote", { quoteId: qq.id }) }));
    // projects
    (store.projects || []).forEach(p => items.push({ kind: "Project", label: p.job + " · " + (p.customer || "Job"), sub: (p.address || "") + " · " + window.VFP.money0(p.total), icon: "clipboard", go: () => go("estimator", { loadProject: p.id }) }));
    // work orders
    (store.workOrders || []).forEach(w => items.push({ kind: "Work Order", label: w.title || w.customer || "Work order", sub: (w.status || "").replace("_", " ") + (w.scheduledStart ? " · " + w.scheduledStart : ""), icon: "calendar", go: () => go("workorders", { focus: w.id }) }));
    // products
    (window.VFP.PRODUCTS || []).forEach(p => items.push({ kind: "Product", label: p.name, sub: p.category + " · " + p.sub, icon: "box", go: () => go("products") }));
    return items;
  }, [store.customers, store.quotes, store.projects, store.workOrders]);

  const results = React.useMemo(() => {
    if (!q.trim()) return index.filter(i => i.kind === "Page").slice(0, 6);
    const t = q.toLowerCase();
    return index.filter(i => (i.label + " " + i.sub + " " + i.kind).toLowerCase().includes(t)).slice(0, 12);
  }, [q, index]);

  function choose(r) { if (!r) return; setOpen(false); setQ(""); r.go(); }

  return (
    <>
      <button className="global-search-trigger" onClick={() => setOpen(true)}>
        <Icon name="search" size={15} className="muted" />
        <span className="gs-long">Search everything…</span>
        <span className="gs-short">Search</span>
        <kbd>⌘K</kbd>
      </button>
      {open && (
        <Portal>
        <div className="gs-overlay" data-dialog onClick={() => setOpen(false)}>
          <div className="gs-modal" onClick={e => e.stopPropagation()}>
            <div className="gs-input">
              <Icon name="search" size={18} className="muted" />
              <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="Search customers, quotes, jobs, products, pages…"
                onKeyDown={e => {
                  if (e.key === "ArrowDown") { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
                  else if (e.key === "ArrowUp") { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
                  else if (e.key === "Enter") { e.preventDefault(); choose(results[active]); }
                }} />
              <kbd>esc</kbd>
            </div>
            <div className="gs-results">
              {results.length === 0
                ? <div className="gs-empty">No matches for "{q}"</div>
                : results.map((r, i) => (
                  <button key={i} className={"gs-item" + (i === active ? " active" : "")} onMouseEnter={() => setActive(i)} onClick={() => choose(r)}>
                    <span className="gs-ico"><Icon name={r.icon} size={15} /></span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="gs-label">{r.label}</span>
                      <span className="gs-sub">{r.sub}</span>
                    </span>
                    <span className="gs-kind">{r.kind}</span>
                  </button>
                ))}
            </div>
          </div>
        </div>
        </Portal>
      )}
    </>
  );
}
Object.assign(window, { GlobalSearch });

export { GlobalSearch };
